/** @jsxImportSource @emotion/react */
import { GlobalStyles, css } from '@mui/material';
import { SupportMenuContainer } from '@/components/SupportMenu/SupportMenuContainer';
import { HEADER_DATA_TEST_ID } from '@/features/layouts/MainLayout/testIds';
import { ROUTERS_PATH } from '@/routers';
import IconSVGTimesMenu from '@/shared/assets/icons/icon-times.svg?react';
import IconSVGHamburger from '@/shared/assets/images/icons/icon_hamburger.svg?react';
import {
  GLOB_NOTIFICATIONS_PER_PAGE,
  UNREAD_GLOB_NOTIFICATIONS_DELAY,
} from '@/shared/constants';
import { envs } from '@/shared/constants/envs';
import { testId } from '@/shared/utils/testId';
import { useAppDispatch } from '@/store';
import { globalNotificationsActions } from '@/store/globalNotifications/slice';
import { ProfileMenuContainer } from '@/features/profile-menu/ProfileMenuContainer';
import { SideMenuContainer } from '@/features/side-menu/SideMenuContainer';
import { FC, useEffect, useState, useCallback, PropsWithChildren } from 'react';
import { Cookies } from 'react-cookie';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import {
  PageBody,
  Header,
  Main,
  GridNavPanel,
  Content,
  GridContainer,
  StyledAppLogo,
  globalStyles,
  MenuLayout,
  IconButton,
  MenuBackdrop,
  FloatMenu,
  BurgerLogoContainer,
} from './styles';
import { useBreakpoints } from 'ui-kit';
import { inheritDataTestId } from 'ui-kit/lib/utils/dataTestId';
import { useInitVivicaLoader } from '@/shared/hooks/useVivicaLoader';
import { useTranslate } from '@/i18n/useTranslate';

const ACCESS_TOKEN_COOKIE_NAME = 'access_token';

export const MainLayout: FC = () => {
  const { ready } = useTranslate('common');
  const { xsOnly, smOnly, mdOnly, lg } = useBreakpoints();
  const tablet = smOnly || mdOnly;
  const [menuOpen, setMenuOpen] = useState(false);

  const [isLogin, setIsLogin] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const isCreateANewPatientPage =
    location.pathname === ROUTERS_PATH.CONNECTIONS_CREATE_A_NEW_PATIENT;
  const hasRelationshipType = location.search.includes('relationshipType');

  const checkLogin = () => {
    const accessTokenCookie = new Cookies().get(ACCESS_TOKEN_COOKIE_NAME);
    const url = envs.PASSPORT_WEB_URL;

    if (accessTokenCookie || !url) {
      setIsLogin(true);
      return;
    }

    const currentLocation = window.location.href;
    window.location.href = `${url}/auth/welcome?backUrl=${currentLocation}`;
  };

  useEffect(() => {
    checkLogin();
  }, []);

  const toggleMenu = useCallback(() => {
    if (!menuOpen) {
      window.document.body.classList.add('sandwich-menu-open');
      window.document.body.scrollTop = 0;
      const selectedMenuItem = window.document
        .querySelector('.grid-nav-panel')
        ?.querySelector('ul > li.selected');
      selectedMenuItem?.scrollIntoView();
    } else {
      window.document.body.classList.remove('sandwich-menu-open');
    }
    setMenuOpen((m) => !m);
  }, [menuOpen]);

  const closeMenu = useCallback(() => {
    window.document.body.classList.remove('sandwich-menu-open');
    setMenuOpen(false);
  }, []);

  useEffect(() => {
    if (!xsOnly && !tablet) {
      window.document.body.classList.remove('sandwich-menu-open');
      setMenuOpen(false);
    }
  }, [xsOnly, tablet]);

  useEffect(() => {
    if (!hasRelationshipType || isCreateANewPatientPage) {
      return;
    }

    const newPath = location.pathname;
    navigate(newPath, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRelationshipType, isCreateANewPatientPage]);

  useEffect(() => {
    const fetchUnreadGlobalNotifications = () => {
      void dispatch(
        globalNotificationsActions.fetchGlobalNotifications({
          startPage: 0,
          perPage: 1,
          read: false,
          isCheckingUnread: true,
        })
      );
    };

    const fetchInitialGlobalNotifications = () => {
      void dispatch(
        globalNotificationsActions.fetchGlobalNotifications({
          startPage: 0,
          perPage: GLOB_NOTIFICATIONS_PER_PAGE,
        })
      );
    };

    fetchUnreadGlobalNotifications();
    fetchInitialGlobalNotifications();

    const intervalId = setInterval(() => {
      fetchUnreadGlobalNotifications();
    }, UNREAD_GLOB_NOTIFICATIONS_DELAY);

    return () => {
      clearInterval(intervalId);
    };
  }, [dispatch]);

  useInitVivicaLoader(ready);

  if (!isLogin) {
    return null;
  }

  return (
    <>
      <GlobalStyles styles={globalStyles} />
      <PageBody>
        <Header>
          <BurgerLogoContainer>
            {(tablet || xsOnly) && (
              <HamburgerButton
                toggle={toggleMenu}
                open={menuOpen}
                data-test-id={testId(HEADER_DATA_TEST_ID.headerBurgerButton)}
              />
            )}
            <Link
              style={{ lineHeight: 0 }}
              to={{ pathname: '/', search: window.location.search }}
              data-test-id={testId(HEADER_DATA_TEST_ID.headerLogoButton)}
            >
              <StyledAppLogo sx={{ mb: { xs: 6, sm: 0 } }} />
            </Link>
          </BurgerLogoContainer>
          <MenuLayout>
            <ProfileMenuContainer />
            {lg && <SupportMenuContainer />}
          </MenuLayout>
        </Header>
        <Main>
          {xsOnly || tablet ? (
            <GridNavPanel
              onClick={closeMenu}
              data-test-id={testId(HEADER_DATA_TEST_ID.bodyBackdrop)}
            >
              <FloatMenu>
                <ScrollbarContainer>
                  <SideMenuContainer open={menuOpen} toggleMenu={toggleMenu} />
                </ScrollbarContainer>
              </FloatMenu>
            </GridNavPanel>
          ) : (
            <SideMenuContainer open={menuOpen} toggleMenu={toggleMenu} />
          )}
          <GridContainer id="main">
            <Content className={'id-content'}>
              <Outlet />
            </Content>
            <MenuBackdrop
              open={menuOpen && (xsOnly || tablet)}
              onClick={toggleMenu}
              data-test-id={testId(HEADER_DATA_TEST_ID.bodyBackdrop)}
            />
          </GridContainer>
        </Main>
      </PageBody>
    </>
  );
};

const HamburgerButton = ({
  toggle,
  open,
  ...props
}: {
  toggle: () => void;
  open: boolean;
}) => {
  return (
    <IconButton onClick={toggle} data-test-id={inheritDataTestId(props)}>
      {open ? <IconSVGTimesMenu /> : <IconSVGHamburger />}
    </IconButton>
  );
};

const ScrollbarContainer: FC<PropsWithChildren> = ({ children }) => {
  const { xsOnly, smOnly, mdOnly } = useBreakpoints();
  const tablet = smOnly || mdOnly;

  if (xsOnly || tablet) {
    return (
      <div
        css={css`
          width: 100%;
          height: 100%;
          overflow-y: auto;
        `}
      >
        {children}
      </div>
    );
  }
  return <PerfectScrollbar>{children}</PerfectScrollbar>;
};
